/**
 * DOM-capture export — grabs the live rendered page (with all inlined Ant Design CSS)
 * and saves it as a standalone HTML file. 100% visual match guaranteed.
 */

export function downloadHTML(html, filename) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Capture the current live DOM as a self-contained HTML file.
 *
 * Steps:
 *   1. Clone <html> (includes all runtime-injected <style> tags from Ant Design)
 *   2. Strip <script> tags so the page is truly static
 *   3. Remove elements marked [data-export-hide] (e.g. export buttons)
 *   4. Inject a small freeze-banner so the viewer knows it's a snapshot
 *   5. Replace relative asset hrefs with absolute URLs (fonts, icons, etc.)
 */
export function capturePageAsHTML(filenameBase) {
  // Clone the entire document
  const clone = document.documentElement.cloneNode(true);

  // 1. Remove all <script> tags → static page
  clone.querySelectorAll('script').forEach(el => el.remove());

  // 2. Remove elements the page marked as "hide on export"
  clone.querySelectorAll('[data-export-hide]').forEach(el => el.remove());

  // 3. Disable all interactive links/buttons so they don't navigate
  clone.querySelectorAll('a[href]').forEach(a => {
    a.removeAttribute('href');
    a.style.cursor = 'default';
  });

  // 4. Fix <link> stylesheet hrefs to absolute (avoids broken relative paths)
  const origin = window.location.origin;
  clone.querySelectorAll('link[rel="stylesheet"]').forEach(link => {
    const href = link.getAttribute('href');
    if (href && href.startsWith('/')) {
      link.setAttribute('href', origin + href);
    }
  });

  // 5. Inject a frozen-snapshot banner at the top of <body>
  const exportedAt = new Date().toLocaleString('th-TH', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
  const banner = document.createElement('div');
  banner.innerHTML = `
    <div style="
      background:#fffbe6;border-bottom:2px solid #ffe58f;
      padding:8px 24px;font-size:13px;color:#614700;
      display:flex;align-items:center;gap:8px;
      position:sticky;top:0;z-index:9999;
    ">
      <span style="font-size:16px">📸</span>
      <strong>Snapshot</strong> — ข้อมูล ณ วันที่ ${exportedAt}
      &nbsp;(ไฟล์นี้ไม่เชื่อมต่อกับฐานข้อมูล)
    </div>`;
  const body = clone.querySelector('body');
  if (body) body.prepend(banner);

  // 6. Serialize and wrap with DOCTYPE
  const html = `<!DOCTYPE html>\n${clone.outerHTML}`;
  const now = new Date().toISOString().slice(0, 10);
  downloadHTML(html, `${filenameBase}_${now}.html`);
}
